/**
 * WebRTC transport for the P2P transfer (browser layer).
 *
 * Establishes an RTCPeerConnection + DataChannel between sender and receiver
 * using `/api/p2p/*`'s ephemeral signaling. ALL signaling payloads (SDP/ICE)
 * travel encrypted with the QR's key (per-role AAD), so the server only
 * relays opaque envelopes: it can't read the SDP or inject its own (without
 * the key it can't produce valid envelopes → MITM impossible).
 *
 * No STUN/TURN servers by default (privacy: zero third parties): with both
 * devices on the same network (the QR case: they're side by side) host/mDNS
 * candidates suffice. Different networks behind NAT = see the guide (Manual).
 */

import {
  decryptEnvelopeText,
  encryptEnvelope,
  isRecord,
  type SignalingClient,
  SignalingError,
  type TransferChannel,
} from "@space/shared";

const POLL_INTERVAL_MS = 700;
const CONNECT_TIMEOUT_MS = 45_000;
const DATA_CHANNEL_LABEL = "space-p2p-transfer";

type SignalPayload =
  | { type: "offer" | "answer"; sdp: string }
  | { type: "ice"; candidate: RTCIceCandidateInit };

/** Validates the peer's decrypted JSON (no assertions on external data). */
function parseSignalPayload(text: string): SignalPayload | null {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  if (!isRecord(value)) {
    return null;
  }
  if ((value.type === "offer" || value.type === "answer") && typeof value.sdp === "string") {
    return { type: value.type, sdp: value.sdp };
  }
  if (value.type === "ice" && isRecord(value.candidate)) {
    const c = value.candidate;
    const candidate: RTCIceCandidateInit = {
      candidate: typeof c.candidate === "string" ? c.candidate : undefined,
      sdpMid: typeof c.sdpMid === "string" ? c.sdpMid : null,
      sdpMLineIndex: typeof c.sdpMLineIndex === "number" ? c.sdpMLineIndex : null,
      usernameFragment: typeof c.usernameFragment === "string" ? c.usernameFragment : null,
    };
    return { type: "ice", candidate };
  }
  return null;
}

export interface RtcPeerHandle {
  channelPromise: Promise<TransferChannel>;
  close(): void;
}

function wrapDataChannel(dc: RTCDataChannel): TransferChannel {
  return {
    send: (data) => dc.send(data),
    get bufferedAmount() {
      return dc.bufferedAmount;
    },
    onMessage(handler) {
      dc.addEventListener("message", (event) => {
        handler(String(event.data));
      });
    },
    onClose(handler) {
      dc.addEventListener("close", () => {
        handler();
      });
    },
  };
}

interface PeerOptions {
  signaling: SignalingClient;
  code: string;
  token: string;
  key: CryptoKey;
  /** AAD we use to encrypt OUR OWN data, and AAD to decrypt the peer's. */
  sendAad: "signal:sender" | "signal:receiver";
  receiveAad: "signal:sender" | "signal:receiver";
  onPeerJoined?: () => void;
  timeoutMs?: number;
}

/**
 * Shared loop: publishes our own ICE, polls the mailbox, and resolves when
 * the DataChannel opens. `close()` stops polling and closes the connection.
 */
function createPeer(
  options: PeerOptions,
  setup: (pc: RTCPeerConnection, onChannel: (dc: RTCDataChannel) => void) => Promise<void>,
  onSignal: (pc: RTCPeerConnection, payload: SignalPayload) => Promise<void>,
): RtcPeerHandle {
  const { signaling, code, token, key, sendAad, receiveAad } = options;
  const pc = new RTCPeerConnection();
  let stopped = false;
  // With the channel open, signaling is no longer needed: we stop polling
  // WITHOUT closing the RTCPeerConnection (closing the room on the server
  // will 404 the peer's next fetch; without this cutoff that 404 would tear
  // down an already-live connection).
  let pollingStopped = false;
  let pollTimer: ReturnType<typeof setTimeout> | undefined;
  // Each side posts its ICE candidates concurrently with its SDP, and the server
  // orders messages by arrival: a candidate can reach the peer before the offer or
  // answer it belongs to. `addIceCandidate` rejects until a remote description is set,
  // so such candidates wait here and are applied right after it.
  const pendingCandidates: RTCIceCandidateInit[] = [];

  const applyCandidate = async (candidate: RTCIceCandidateInit) => {
    if (pc.remoteDescription) {
      await pc.addIceCandidate(candidate);
    } else {
      pendingCandidates.push(candidate);
    }
  };

  const flushPendingCandidates = async () => {
    await Promise.all(
      pendingCandidates.splice(0).map((candidate) => pc.addIceCandidate(candidate)),
    );
  };

  const stopPolling = () => {
    pollingStopped = true;
    if (pollTimer !== undefined) {
      clearTimeout(pollTimer);
    }
  };

  const stop = () => {
    stopped = true;
    stopPolling();
    try {
      pc.close();
    } catch {
      // already closed
    }
  };

  const channelPromise = new Promise<TransferChannel>((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error("p2p-connect-timeout"));
      stop();
    }, options.timeoutMs ?? CONNECT_TIMEOUT_MS);

    const onChannel = (dc: RTCDataChannel) => {
      dc.addEventListener("open", () => {
        clearTimeout(timeout);
        stopPolling();
        // The room has already served its purpose: it's closed on the server
        // (hygiene and reinforcement of one-time use). Best-effort.
        void signaling.closeRoom(code, token).catch(() => undefined);
        resolve(wrapDataChannel(dc));
      });
    };

    pc.addEventListener("icecandidate", (event) => {
      if (!event.candidate || stopped) {
        return;
      }
      const payload: SignalPayload = { type: "ice", candidate: event.candidate.toJSON() };
      void (async () => {
        try {
          await signaling.postMessage(
            code,
            token,
            await encryptEnvelope(key, JSON.stringify(payload), sendAad),
          );
        } catch {
          // the room may have already been closed with the channel open: ignorable
        }
      })();
    });

    pc.addEventListener("connectionstatechange", () => {
      if (pc.connectionState === "failed") {
        clearTimeout(timeout);
        reject(new Error("p2p-connection-failed"));
        stop();
      }
    });

    let after = 0;
    let announcedPeer = false;
    const poll = async () => {
      if (stopped || pollingStopped) {
        return;
      }
      try {
        const inbox = await signaling.fetchMessages(code, token, after);
        if (inbox.peerJoined && !announcedPeer) {
          announcedPeer = true;
          options.onPeerJoined?.();
        }
        for (const message of inbox.messages) {
          after = Math.max(after, message.seq);
          // Invalid envelope (wrong key / tampering) → abort NOW.
          const payload = parseSignalPayload(
            await decryptEnvelopeText(key, message.payload, receiveAad),
          );
          if (!payload) {
            throw new Error("p2p-signaling-failed");
          }
          if (payload.type === "ice") {
            await applyCandidate(payload.candidate);
          } else {
            await onSignal(pc, payload);
            await flushPendingCandidates();
          }
        }
      } catch (error) {
        if (!stopped && !pollingStopped) {
          clearTimeout(timeout);
          reject(error instanceof SignalingError ? error : new Error("p2p-signaling-failed"));
          stop();
        }
        return;
      }
      if (!stopped && !pollingStopped) {
        pollTimer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
      }
    };

    void (async () => {
      try {
        await setup(pc, onChannel);
        await poll();
      } catch (error) {
        clearTimeout(timeout);
        reject(error instanceof SignalingError ? error : new Error("p2p-setup-failed"));
        stop();
      }
    })();
  });

  // Avoids "unhandled rejection" if the consumer only uses close() after a failure.
  channelPromise.catch(() => undefined);

  return { channelPromise, close: stop };
}

/** SENDER: creates the DataChannel, publishes the encrypted offer and awaits the response. */
export function connectAsSender(options: {
  signaling: SignalingClient;
  code: string;
  senderToken: string;
  key: CryptoKey;
  onPeerJoined?: () => void;
  timeoutMs?: number;
}): RtcPeerHandle {
  const { signaling, code, senderToken, key } = options;
  return createPeer(
    {
      signaling,
      code,
      token: senderToken,
      key,
      sendAad: "signal:sender",
      receiveAad: "signal:receiver",
      onPeerJoined: options.onPeerJoined,
      timeoutMs: options.timeoutMs,
    },
    async (pc, onChannel) => {
      onChannel(pc.createDataChannel(DATA_CHANNEL_LABEL));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const payload: SignalPayload = { type: "offer", sdp: offer.sdp ?? "" };
      await signaling.postMessage(
        code,
        senderToken,
        await encryptEnvelope(key, JSON.stringify(payload), "signal:sender"),
      );
    },
    async (pc, payload) => {
      if (payload.type === "answer") {
        await pc.setRemoteDescription({ type: "answer", sdp: payload.sdp });
      }
    },
  );
}

/** RECEIVER: awaits the encrypted offer, responds and exposes the incoming channel. */
export function connectAsReceiver(options: {
  signaling: SignalingClient;
  code: string;
  receiverToken: string;
  key: CryptoKey;
  timeoutMs?: number;
}): RtcPeerHandle {
  const { signaling, code, receiverToken, key } = options;
  return createPeer(
    {
      signaling,
      code,
      token: receiverToken,
      key,
      sendAad: "signal:receiver",
      receiveAad: "signal:sender",
      timeoutMs: options.timeoutMs,
    },
    async (pc, onChannel) => {
      pc.addEventListener("datachannel", (event) => {
        onChannel(event.channel);
      });
    },
    async (pc, payload) => {
      if (payload.type === "offer") {
        await pc.setRemoteDescription({ type: "offer", sdp: payload.sdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        const out: SignalPayload = { type: "answer", sdp: answer.sdp ?? "" };
        await signaling.postMessage(
          code,
          receiverToken,
          await encryptEnvelope(key, JSON.stringify(out), "signal:receiver"),
        );
      }
    },
  );
}
